import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { tenantsQueryKey } from '@/features/tenants/queries'

import {
  deleteDiscoverySource,
  deleteDocument,
  getImportStatus,
  listDiscoverySources,
  listKnowledgeDocuments,
  startDiscovery,
  updateWebsite,
} from './api'
import { isDiscoveryRunning, isImportTerminal } from './discovery'
import { asNumber } from './types'

const STATUS_POLL_MS = 2_500
const DOCUMENTS_POLL_MS = 4_000
const IMPORT_POLL_MS = 2_500

export function discoverySourcesQueryKey(tenantId: string) {
  return ['knowledge', 'discovery', tenantId] as const
}

export function knowledgeDocumentsQueryKey(tenantId: string) {
  return ['knowledge', 'documents', tenantId] as const
}

export function importStatusQueryKey(tenantId: string, importId: string) {
  return ['knowledge', 'import', tenantId, importId] as const
}

/**
 * Poll das fontes de aprendizado. Segue repetindo enquanto QUALQUER fonte estiver em andamento
 * (Queued/Crawling/Indexing); para quando todas chegam a um estado terminal (Ready/Failed) ou nao ha fonte.
 */
export function useDiscoverySources(tenantId: string | undefined) {
  return useQuery({
    queryKey: discoverySourcesQueryKey(tenantId ?? ''),
    queryFn: () => listDiscoverySources(tenantId ?? ''),
    enabled: tenantId !== undefined,
    refetchInterval: (query) => {
      const data = query.state.data
      if (data === undefined || data.length === 0) {
        return false
      }
      return data.some((source) => isDiscoveryRunning(source.status)) ? STATUS_POLL_MS : false
    },
  })
}

/** Documentos aprendidos; segue atualizando enquanto houver paginas na fila/processando. */
export function useKnowledgeDocuments(tenantId: string | undefined) {
  return useQuery({
    queryKey: knowledgeDocumentsQueryKey(tenantId ?? ''),
    queryFn: () => listKnowledgeDocuments(tenantId ?? ''),
    enabled: tenantId !== undefined,
    refetchInterval: (query) => {
      const data = query.state.data
      if (data === undefined) {
        return false
      }
      return asNumber(data.pending) + asNumber(data.processing) > 0 ? DOCUMENTS_POLL_MS : false
    },
  })
}

/**
 * Poll do status de importacao de um arquivo enviado. Habilita apenas quando ha um `importId`
 * (apos o 202 do upload) e para de repetir ao chegar a um estado terminal (Ready/Failed).
 */
export function useImportStatus(tenantId: string, importId: string | undefined) {
  return useQuery({
    queryKey: importStatusQueryKey(tenantId, importId ?? ''),
    queryFn: () => getImportStatus(tenantId, importId ?? ''),
    enabled: importId !== undefined,
    refetchInterval: (query) => {
      const data = query.state.data
      if (data === undefined) {
        return IMPORT_POLL_MS
      }
      return isImportTerminal(data.status) ? false : IMPORT_POLL_MS
    },
    // Continua o poll mesmo com a aba em segundo plano: o resultado (inclusive uma falha) tem de aparecer quando
    // o usuario volta, sem depender de foco — evita ficar "preso" em Processando enquanto o job ja terminou/falhou.
    refetchIntervalInBackground: true,
  })
}

/** Define/atualiza o site da organizacao (habilita o "Ensine seu agente" quando o site foi pulado). */
export function useUpdateWebsite(tenantId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (website: string) => updateWebsite(tenantId ?? '', website),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantsQueryKey })
    },
  })
}

/**
 * Adiciona/dispara uma fonte. `url` = aprende com aquela URL; `undefined` = usa o site cadastrado.
 * Invalida a lista de fontes para a nova aparecer com seu progresso. Sempre chamar com um argumento
 * explicito (`mutate(url)` ou `mutate(undefined)`).
 */
export function useStartDiscovery(tenantId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (url: string | undefined) => startDiscovery(tenantId ?? '', url),
    onSuccess: () => {
      if (tenantId !== undefined) {
        void queryClient.invalidateQueries({ queryKey: discoverySourcesQueryKey(tenantId) })
        void queryClient.invalidateQueries({ queryKey: knowledgeDocumentsQueryKey(tenantId) })
      }
    },
  })
}

/** Remove uma fonte de aprendizado. Idempotente no backend; invalida a lista + documentos aprendidos. */
export function useDeleteDiscoverySource(tenantId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (jobId: string) => deleteDiscoverySource(tenantId ?? '', jobId),
    onSuccess: () => {
      if (tenantId !== undefined) {
        void queryClient.invalidateQueries({ queryKey: discoverySourcesQueryKey(tenantId) })
        void queryClient.invalidateQueries({ queryKey: knowledgeDocumentsQueryKey(tenantId) })
      }
    },
  })
}

/** Exclui um documento aprendido. Idempotente no backend; invalida a lista de documentos. */
export function useDeleteDocument(tenantId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (documentId: string) => deleteDocument(tenantId ?? '', documentId),
    onSuccess: () => {
      if (tenantId !== undefined) {
        void queryClient.invalidateQueries({ queryKey: knowledgeDocumentsQueryKey(tenantId) })
      }
    },
  })
}
